import { useQuery, requestDeals } from '@workspace/api';

export interface MessageRecipient {
    type: string;
    name: string;
    email: string;
}

export interface MessageContext {
    type: string;
    value: number;
}

export interface MessageHistoryEvent {
    event: string;
    name: string | null;
    created_at: string;
}

export interface Message {
    id: number;
    type: 'email' | 'sms';
    sent_at: string | null;
    created_at: string;
    subject: string | null;
    template_slug: string | null;
    template_title: string | null;
    department: string | null;
    profile: string;
    author: string | null;
    recipients: MessageRecipient[];
    contexts: MessageContext[];
    history: MessageHistoryEvent[];
}

export interface MessagesResponse {
    data: Message[];
    meta?: {
        current_page: number;
        from: number | null;
        last_page: number;
        per_page: number;
        to: number | null;
        total: number;
    };
}

export interface MessageFilters {
    page?: number;
    search?: string;
    date_from?: string;
    date_to?: string;
}

/**
 * Hook pour récupérer les messages (emails & SMS) d'un devis (avec pagination et filtres)
 */
export function useQuoteMessages(quoteId: number | null | undefined, filters: MessageFilters = {}) {
    const { page = 1, search, date_from, date_to } = filters;

    return useQuery<MessagesResponse>({
        queryKey: ['deals', 'quotes', quoteId, 'messages', { page, search, date_from, date_to }],
        queryFn: async () => {
            const params: Record<string, string | number> = { page };
            if (search) params.search = search;
            if (date_from) params.date_from = date_from;
            if (date_to) params.date_to = date_to;

            const response = await requestDeals.get<MessagesResponse>(`/api/quotes/${quoteId}/messages`, {
                params,
            });
            return response.data;
        },
        staleTime: 5 * 60 * 1000,
        enabled: !!quoteId,
    });
}

/**
 * Hook pour récupérer les messages (emails & SMS) d'un contact (avec pagination et filtres)
 */
export function useContactMessages(contactId: number | string | null | undefined, filters: MessageFilters = {}) {
    const { page = 1, search, date_from, date_to } = filters;

    return useQuery<MessagesResponse>({
        queryKey: ['deals', 'contacts', contactId, 'messages', { page, search, date_from, date_to }],
        queryFn: async () => {
            const params: Record<string, string | number> = { page };
            if (search) params.search = search;
            if (date_from) params.date_from = date_from;
            if (date_to) params.date_to = date_to;

            const response = await requestDeals.get<MessagesResponse>(`/api/contacts/${contactId}/messages`, {
                params,
            });
            return response.data;
        },
        staleTime: 5 * 60 * 1000,
        enabled: !!contactId,
    });
}
